/**
 * Pure, React-free, DOM-free helpers for the BatchReviewPage "print this run's
 * label" action (Run Batch Label Printing feature).
 *
 * The visibility gate (is the loaded Capture_Batch's `runId` a Usable_Run_Id?)
 * and the resolution-outcome → panel-state mapping are kept here as pure
 * exported functions so they can be unit- and property-tested without a DOM
 * (the frontend jest env is `node`). The React `RunLabelPanel` imports these
 * helpers and renders the plain data structures they return.
 *
 * Nothing in this file imports React, a browser API, or an AWS SDK. The wire
 * shape `RunBatchResolution` comes from `@tcvs/shared` (via `../shared/types`)
 * and is never redeclared.
 *
 * Validates: Requirements 4.1, 4.2, 5.1, 5.2
 */

import type { ApiResult } from '../auth/api-client';
import type { RunBatchResolution } from '../shared/types';

// ---------------------------------------------------------------------------
// Visibility gate — Usable_Run_Id predicate (Req 4.1, 4.2)
// ---------------------------------------------------------------------------

/**
 * Return the run id only when it is a Usable_Run_Id — a string with
 * non-whitespace content — otherwise `undefined` (Req 4.1, 4.2).
 *
 * A Capture_Batch carries `runId` as a non-empty UUID v4 (a real Capture_Run),
 * `null` (single-frame captures fired outside a run), or `undefined` (legacy
 * uploads pre-dating the `runId` schema). Only the first case is resolvable to
 * a Storage_Batch, so the "print this run's label" action is shown if and only
 * if this predicate yields a defined run id. The original (untrimmed) string is
 * returned so the caller resolves against the exact run id it was given.
 */
export function usableRunId(runId: string | null | undefined): string | undefined {
  return typeof runId === 'string' && runId.trim() !== '' ? runId : undefined;
}

// ---------------------------------------------------------------------------
// Panel state machine (Req 4.6, 5.1, 5.2)
// ---------------------------------------------------------------------------

/**
 * The discriminated state of the run-label panel. `idle` before the Operator
 * asks for the label, `loading` while a Run_Batch_Resolution request is in
 * flight (Req 4.6), `resolved` once the batch's label is available (Req 4.4),
 * `not-found` when the run has no Storage_Batch yet (Req 5.1), and `error` for
 * any other failure, carrying a retryable message (Req 5.2).
 */
export type RunLabelState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'resolved'; data: RunBatchResolution }
  | { status: 'not-found' }
  | { status: 'error'; message: string };

/**
 * Map a `resolveRunBatch` outcome to the panel state to render (Req 5.1, 5.2).
 *
 * Every outcome partitions into exactly one surface:
 * - a resolved success → `resolved`, carrying the data to render as a label;
 * - a 404 failure → `not-found`, the distinct "run has no batch yet" surface
 *   (the by-run route returns 404 `not-found` for a Usable_Run_Id with no
 *   batch, separate from the 400 invalid-id case) (Req 5.1);
 * - any other failure → `error`, carrying a message and driving the Retry
 *   control (Req 5.2).
 */
export function mapResolutionOutcome(
  result: ApiResult<RunBatchResolution>,
): RunLabelState {
  if (result.ok) {
    return { status: 'resolved', data: result.data };
  }
  if (result.status === 404) {
    return { status: 'not-found' };
  }
  return { status: 'error', message: result.message ?? result.error };
}
